"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CcTokenizerFrame = void 0;
const React = require("react");
const react_1 = require("react");
const LOAD_TIMEOUT_MS = 10000;
/**
 * The frame is uncovered on the cssLoaded message so the unstyled form is never
 * shown. If the hosted page rejects our stylesheet that message never arrives,
 * so reveal anyway rather than leaving the skeleton up until LOAD_TIMEOUT_MS.
 */
const CSS_REVEAL_FALLBACK_MS = 1500;
/**
 * "Required" codes fire while the user is still filling the form — the card
 * number validates a second after typing stops, before expiry/CVV are reached.
 * Treated as incomplete (token cleared, Submit stays disabled) but not shown
 * as an error; only genuinely invalid input gets a red message.
 */
const INCOMPLETE_ERROR_CODES = ["1004", "1005", "1006"];
/**
 * Injected into the tokenizer iframe. Element ids come from the hosted page:
 * #ccnumfield, #cccvvfield, and — because useexpiryfield is off — the
 * #ccexpirymonth / #ccexpiryyear selects (the text-input variant would instead
 * be #ccexpiryfieldmonth / #ccexpiryfieldyear).
 * Width is set per field — a blanket `input{width:100%}` forces the month,
 * year and CVV boxes onto their own rows and pushes CVV out of the frame.
 */
const TOKENIZER_CSS = [
    "body{margin:0;padding:0;font-family:Roboto,Helvetica,Arial,sans-serif;color:#212121;}",
    "label{color:#616161;font-size:11px;font-weight:600;letter-spacing:0.04em;",
    "text-transform:uppercase;}",
    "input,select{background-color:#fff;border:1px solid #ced4da;border-radius:4px;",
    "box-sizing:border-box;font-size:15px;line-height:1.5;padding:8px 12px;",
    "margin:2px 0 6px 0;}",
    "input:focus,select:focus{border-color:#10b505;outline:none;}",
    "#ccnumfield{width:100%;}",
    "#ccexpirymonth{width:84px;}",
    "#ccexpiryyear{width:104px;}",
    "#cccvvfield{width:90px;}",
    "input.error,select.error{border-color:#d32f2f;}"
].join("");
const buildTokenizerSrc = (baseUrl, cacheBust) => {
    const params = {
        useexpiry: "true",
        // Dropdowns rather than text inputs: month and year are <select> lists, so
        // a valid MM and a 4-digit YYYY cannot be mistyped or over-typed at all.
        useexpiryfield: "false",
        usecvv: "true",
        formatinput: "true",
        enhancedresponse: "true",
        cardnumbernumericonly: "true",
        // 19 digits (the longest PAN the gateway accepts) plus the 4 spaces
        // formatinput adds. Assumes keyed entry only — a card swiper types far
        // longer track data and would be truncated by this cap.
        cardinputmaxlength: "23",
        cvvnumericonly: "true",
        invalidinputevent: "true",
        invalidcreditcardevent: "true",
        invalidexpiryevent: "true",
        invalidcvvevent: "true",
        sendcardtypingevent: "true",
        sendcssloadedevent: "true",
        tokenizewheninactive: "true",
        inactivityto: "1000",
        // Labels are read with decodeURI() in the iframe, which leaves reserved
        // characters (/ ? : & = + # , ; @ $) escaped — keep them to letters/spaces.
        cardlabel: "Card Number",
        expirylabel: "Expiration Date",
        cvvlabel: "CVV",
        placeholder: "1234 5678 9012 3456",
        placeholdercvv: "123",
        css: TOKENIZER_CSS,
        cachebust: String(cacheBust)
    };
    // Not URLSearchParams: it form-encodes spaces as "+", and the hosted page
    // reads labels with decodeURI(), which leaves the "+" in place.
    const query = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join("&");
    return `${baseUrl}?${query}`;
};
const tokenizerOrigin = (iframeUrl) => {
    try {
        return new URL(iframeUrl).origin;
    }
    catch (_a) {
        return "";
    }
};
/**
 * D-002 Phase 3 — CardPointe Hosted iFrame Tokenizer.
 * Card number / expiry / CVV never touch TMPP; we only receive a CardSecure token.
 */
class CcTokenizerFrame extends react_1.Component {
    constructor(props) {
        super(props);
        this.loadTimer = null;
        this.revealTimer = null;
        this.hasReceivedMessage = false;
        this.cacheBust = 0;
        this.setStatus = (status) => {
            this.setState({ status });
            if (this.props.onStatusChange) {
                this.props.onStatusChange(status);
            }
        };
        this.startLoadTimer = () => {
            this.clearLoadTimer();
            this.hasReceivedMessage = false;
            this.loadTimer = window.setTimeout(() => {
                if (!this.hasReceivedMessage && this.state.status === "loading") {
                    this.setStatus("failed");
                    this.props.onError("Card entry could not load. Refresh the page or contact support.");
                }
            }, LOAD_TIMEOUT_MS);
        };
        this.clearLoadTimer = () => {
            if (this.loadTimer != null) {
                window.clearTimeout(this.loadTimer);
                this.loadTimer = null;
            }
        };
        this.clearRevealTimer = () => {
            if (this.revealTimer != null) {
                window.clearTimeout(this.revealTimer);
                this.revealTimer = null;
            }
        };
        this.handleIframeLoad = () => {
            // The document is up but CardPointe has not applied the stylesheet yet;
            // stay covered until cssLoaded arrives, or until the fallback expires.
            this.clearRevealTimer();
            this.revealTimer = window.setTimeout(() => {
                this.revealTimer = null;
                if (this.state.status === "loading") {
                    this.setStatus("ready");
                }
            }, CSS_REVEAL_FALLBACK_MS);
        };
        this.remountFrame = (iframeUrl, resetKey) => {
            this.cacheBust = resetKey || Date.now();
            this.clearRevealTimer();
            this.props.onClearToken();
            this.setState({
                status: "loading",
                validationError: "",
                src: buildTokenizerSrc(iframeUrl, this.cacheBust)
            });
            this.setStatus("loading");
            this.startLoadTimer();
        };
        this.handleRetry = () => {
            this.remountFrame(this.props.iframeUrl, Date.now());
        };
        this.handleReset = () => {
            this.remountFrame(this.props.iframeUrl, Date.now());
        };
        /** No token and nothing to complain about — back to a clean entry state. */
        this.clearToken = () => {
            this.props.onClearToken();
            this.setState({ validationError: "" });
            this.setStatus("ready");
        };
        this.handleMessage = (event) => {
            const expectedOrigin = tokenizerOrigin(this.props.iframeUrl);
            if (!expectedOrigin || event.origin !== expectedOrigin) {
                return;
            }
            let payload = event.data;
            if (typeof payload === "string") {
                try {
                    payload = JSON.parse(payload);
                }
                catch (_a) {
                    return;
                }
            }
            if (!payload || typeof payload !== "object") {
                return;
            }
            this.hasReceivedMessage = true;
            this.clearLoadTimer();
            if (payload.cssLoaded === true) {
                this.clearRevealTimer();
                if (this.state.status === "loading") {
                    this.setStatus("ready");
                }
                return;
            }
            if (payload.cardTyping === true) {
                this.props.onClearToken();
                this.setState({ validationError: "" });
                this.setStatus("typing");
                return;
            }
            if (payload.cardTyping === false) {
                if (this.state.status === "typing") {
                    this.setStatus("ready");
                }
                return;
            }
            const errorCode = payload.errorCode != null ? String(payload.errorCode) : "";
            const errorMessage = String(payload.validationError || payload.errorMessage || "");
            const hasError = !!payload.validationError || (!!errorCode && errorCode !== "0");
            if (hasError) {
                this.props.onClearToken();
                if (INCOMPLETE_ERROR_CODES.indexOf(errorCode) >= 0) {
                    // Still being filled in — don't nag mid-entry.
                    this.setState({ validationError: "" });
                    this.setStatus("ready");
                    return;
                }
                const message = errorMessage || "Check the card details and try again.";
                this.setState({ validationError: message });
                this.setStatus("invalid");
                this.props.onError(message);
                return;
            }
            const token = String(payload.message || payload.token || "").trim();
            if (token) {
                this.setState({ validationError: "" });
                this.setStatus("tokenized");
                this.props.onToken(token);
                return;
            }
            // Editing expiry or CVV after tokenizing clears the card in the iframe and
            // posts an empty message; without this the stale token stays submittable.
            if (Object.prototype.hasOwnProperty.call(payload, "message")) {
                this.clearToken();
            }
        };
        this.cacheBust = props.resetKey || Date.now();
        this.state = {
            status: "loading",
            validationError: "",
            src: buildTokenizerSrc(props.iframeUrl, this.cacheBust)
        };
    }
    componentDidMount() {
        window.addEventListener("message", this.handleMessage, false);
        this.startLoadTimer();
    }
    componentWillReceiveProps(nextProps) {
        if (nextProps.resetKey != null &&
            nextProps.resetKey !== this.props.resetKey) {
            this.remountFrame(nextProps.iframeUrl, nextProps.resetKey);
        }
        else if (nextProps.iframeUrl !== this.props.iframeUrl) {
            this.remountFrame(nextProps.iframeUrl, this.cacheBust);
        }
    }
    componentWillUnmount() {
        window.removeEventListener("message", this.handleMessage, false);
        this.clearLoadTimer();
        this.clearRevealTimer();
    }
    render() {
        const { status, validationError, src } = this.state;
        return (React.createElement("div", { className: "payment__tokenizer" },
            React.createElement("div", { className: "payment__tokenizer-frame-wrap" },
                status === "loading" && (React.createElement("div", { className: "payment__tokenizer-skeleton", "aria-hidden": "true" })),
                status !== "failed" && (React.createElement("iframe", { key: this.cacheBust, title: "CardPointe card entry", className: status === "loading"
                        ? "payment__tokenizer-iframe payment__tokenizer-iframe--hidden"
                        : "payment__tokenizer-iframe", src: src, frameBorder: 0, scrolling: "no", onLoad: this.handleIframeLoad }))),
            status === "failed" && (React.createElement("div", { className: "payment__tokenizer-error", role: "alert" },
                "Card entry could not load. Refresh the page or contact support.",
                React.createElement("button", { type: "button", className: "payment__tokenizer-retry", onClick: this.handleRetry }, "Retry"))),
            status === "invalid" && validationError && (React.createElement("div", { className: "payment__tokenizer-error", role: "alert" }, validationError)),
            status === "tokenized" && (React.createElement("div", { className: "payment__tokenizer-ready" },
                "Card ready",
                React.createElement("button", { type: "button", className: "payment__tokenizer-reset", onClick: this.handleReset }, "Use a different card")))));
    }
}
exports.CcTokenizerFrame = CcTokenizerFrame;



//////////////////
// WEBPACK FOOTER
// ./src/client/modules/orders/modules/orders-detail/tabs/payment/cc-tokenizer-frame.tsx
// module id = 3411
// module chunks = 0